import React, { useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import DtoPiezometerChartOptions from '../../../../station/dto/piezometer/DtoPiezometerChartOptions'
import { arrayOf } from '../../../../utils/StoreUtils'
import NumberField from '../../../../components/forms/NumberField'
import Checkbox from '../../../../components/forms/Checkbox'
import { hasValue, round } from '../../../../utils/NumberUtil'
import RadioButtons from '../../../../components/forms/RadioButtons'
import { getMeasureValue, getNGFValue } from '../../../../utils/PiezometryUtils'
import Button from '../../../../components/forms/Button'
import PiezometerStationAction from '../../../../station/actions/PiezometerStationAction'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { CHARTOPTIONS } from '../../suivi/constants/PiezometerSuiviConstants'
import { useDispatch } from 'react-redux'
import { execByType } from '../../../../utils/StationUtils'
import HydrometerStationAction from '../../../../station/actions/HydrometerStationAction'
import { PIEZO_TYPEID } from 'piezometry/constants/PiezometryConstants'
import { Card, CardContent, Grid2, Stack } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'

const AUTO = 'auto'

// ATTENTION : composant réutilisé sur piézo (suivi et validation), et hydro (suivi et validation)
const OptionsToolPanel = ({
    tab = CHARTOPTIONS,
    chartOptions,
    typeId,
    isPiezo,
    displayCote,
    landmarkValue,
    changeParent = () => { },
    id,
    modeDate,
    yScale,
    groundRefAlti,
    withDateFormat = true,
    stationType,
}) => {
    const dispatch = useDispatch()
    const [validateActive, setValidateActive] = useState(false)
    const [initialOptions, setInitialOptions] = useState(chartOptions)


    const getPiezoValue = (value) => {
        if (!isPiezo) {
            return value
        }
        if (hasValue(value)) {
            return round(getMeasureValue({ NGF: value }, displayCote, landmarkValue, groundRefAlti), 2)
        }
        return null
    }

    const getPiezoNGF = (value) => {
        if (!isPiezo) {
            return value
        }
        if (hasValue(value)) {
            return round(getNGFValue({ value }, displayCote, landmarkValue, groundRefAlti), 2)
        }
        return null
    }

    const onCancel = () => {
        changeParent({ tabLocked: false, chartOptions: initialOptions })
        setValidateActive(false)
    }

    const onSave = () => {
        const promise = execByType(stationType, {
            piezometry: () => PiezometerStationAction.updatePiezometerChartOptions,
            hydrometry: () => HydrometerStationAction.updateHydrometerChartOptions,
        })
        dispatch(promise(id, chartOptions, () => {
            setValidateActive(false)
            setInitialOptions(chartOptions)
            changeParent({ tabLocked: false })
        }))
    }

    const chartOption = chartOptions.find(d => parseInt(d.dataType ?? PIEZO_TYPEID.CHRONIC) === typeId) || new DtoPiezometerChartOptions({
        dataType: typeId.toString(),
        displayXIntervalYear: '1',
        displayYIntervalYear: '1',
        displayData: true,
    })
    const change = (changes) => {
        const newOption = { ...chartOption, ...changes }
        changeParent({ tabLocked: true, chartOptions: [...chartOptions.filter(c => parseInt(c.dataType ?? PIEZO_TYPEID.CHRONIC) !== typeId), newOption] })
        setValidateActive(true)
    }
    const scales = [
        { value: AUTO, label: i18n.automatic },
        { value: 'manual', label: i18n.manual },
    ]
    const mode = hasValue(chartOption.maxYear) || hasValue(chartOption.minYear) ? 'manual' : AUTO
    if (tab !== CHARTOPTIONS) {
        return null
    }
    const modeDateList = [
        {
            code: 'allDate',
            name: 'DD/MM/YYYY hh:mm:ss',
        }, {
            code: 'dailyDate',
            name: 'DD/MM/YYYY',
        },
    ]
    return (
        <Card>
            <CardTitle title={i18n.chartOptions} />
            <CardContent>
                <Stack spacing={1}>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.gridsDisplay}</StyledLegend>
                        <Grid2 container>
                            <Grid2 size={6}>
                                <Checkbox
                                    checked={hasValue(chartOption.displayXIntervalYear) ? chartOption.displayXIntervalYear === '1' : true}
                                    label={i18n.Xgrid}
                                    onChange={v => change({ displayXIntervalYear: v ? '1' : '0' })}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <Checkbox
                                    checked={hasValue(chartOption.displayYIntervalYear) ? chartOption.displayYIntervalYear === '1' : true}
                                    label={i18n.Ygrid}
                                    onChange={v => change({ displayYIntervalYear: v ? '1' : '0' })}
                                />
                            </Grid2>
                        </Grid2>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.yScale}</StyledLegend>
                        <RadioButtons
                            elements={scales}
                            selected={mode}
                            onChange={v => {
                                if (v !== mode) {
                                    if (v === 'manual') {
                                        change({ maxYear: getPiezoNGF(yScale.max), minYear: getPiezoNGF(yScale.min) })
                                    }
                                    if (v === AUTO) {
                                        change({ maxYear: undefined, minYear: undefined, intervalYear: undefined })
                                    }
                                }
                            }}
                        />
                        <NumberField
                            value={chartOption.intervalYear} min={1}
                            title={i18n.intervalCount}
                            onChange={v => change({ intervalYear: v })}
                            disabled={mode === AUTO}
                        />
                        <NumberField
                            disabled={mode === AUTO}
                            value={getPiezoValue(chartOption.maxYear)}
                            title={i18n.max}
                            onChange={v => change({ maxYear: getPiezoNGF(v) })} floatValue
                        />
                        <NumberField
                            disabled={mode === AUTO}
                            value={getPiezoValue(chartOption.minYear)}
                            title={i18n.min}
                            onChange={v => change({ minYear: getPiezoNGF(v) })} floatValue
                        />
                    </StyledFieldSet>
                    {
                        withDateFormat && (
                            <StyledFieldSet>
                                <StyledLegend>{i18n.formatDate}</StyledLegend>
                                <RadioButtons selected={modeDate} onChange={v => changeParent({ modeDate: v })} elements={modeDateList} />
                            </StyledFieldSet>
                        )
                    }
                    <Grid2 container justifyContent='center'>
                        <Button
                            tooltip={i18n.cancel}
                            onClick={onCancel}
                            icon='cancel'
                            className='red btn-floating btn-large margin-left-2 margin-right-2 z-index-10 '
                            disabled={!validateActive}
                        />
                        <Button
                            tooltip={i18n.register}
                            onClick={onSave}
                            icon='save'
                            disabled={!validateActive}
                            className={`btn-floating btn-large z-index-10 ${validateActive ? 'pulse' : ''}`}
                        />
                    </Grid2>
                </Stack>
            </CardContent>
        </Card>
    )
}

OptionsToolPanel.propTypes = {
    tab: PropTypes.string,
    id: PropTypes.string,
    chartOptions: arrayOf(DtoPiezometerChartOptions),
    changeParent: PropTypes.func,
    dataType: PropTypes.number,
    yScale: PropTypes.objectOf(PropTypes.number),
    displayCote: PropTypes.number,
    landmarkValue: PropTypes.number,
    groundRefAlti: PropTypes.number,
    modeDate: PropTypes.string,
    typeId: PropTypes.number,
    isPiezo: PropTypes.bool,
    withDateFormat: PropTypes.bool,
    stationType: PropTypes.string,
}

export default OptionsToolPanel